import Dialog from '@mui/material/Dialog'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import { Box, Link } from '@mui/material'
import { Field, FieldWrapper, Form, FormElement } from '@progress/kendo-react-form'
import { Button as KButton } from '@progress/kendo-react-buttons'
import { Loader } from '@progress/kendo-react-indicators'
import { Input } from '@progress/kendo-react-inputs'
import { Label } from '@progress/kendo-react-labels'
import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import Button from 'components/Common/Button'

import api from 'api'
import { DISABLED_BUTTON, PRIMARY_BUTTON } from 'themes/theme'

const sx = {
  shieldIcon: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    backgroundColor: 'rgb(71,45,233)',
  },
  dialogLink: {
    color: 'rgb(71,45,233)',
  },
  leftBlock: {
    backgroundColor: 'rgb(246,246,249)',
  },
  dialogWrapper: (theme) => ({
    [theme.breakpoints.up('md')]: {
      height: '500px',
    },
    [theme.breakpoints.up('lg')]: {
      height: '700px',
    },
  }),
  submitButton: {
    padding: '7px 25px',
    borderRadius: '50px',
    backgroundColor: 'rgb(71,45,233) !important',
    fontSize: '1.5rem',
    '&:hover': {
      backgroundColor: 'rgba(71,45,233, 0.8) !important',
      borderColor: 'rgba(71,45,233, 0.8) !important',
    },
    '&:disabled': {
      backgroundColor: 'rgb(71,45,233) !important',
      borderColor: 'rgba(71,45,233, 0.8) !important',
    },
  },
  dialogCloseButton: {
    position: 'absolute',
    right: '15px',
    top: '15px',
  },
  close: {
    background: 'transparent',
    borderRadius: '5px',
    fontWeight: 'bold',
  },
}

const inputValidator = value => (!value ? 'Please enter a your company name.' : '')

const FormInput = fieldRenderProps => {
  const { validationMessage, touched, label, id, valid, disabled, hint, type, optional, max, value, ...others } =
    fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hindId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''

  return (
    <FieldWrapper>
      <Label editorId={id} editorDisabled={disabled} optional={optional} className="fw-bolder mb-2 text-black">
        {label}
      </Label>
      <div className={'k-form-field-wrap'}>
        <Input
          valid={valid}
          type={type}
          id={id}
          disabled={disabled}
          maxLength={max}
          ariaDescribedBy={`${hindId} ${errorId}`}
          className="p-3 mb-3"
          {...others}
        />
      </div>
    </FieldWrapper>
  )
}

const ConnectAppModal = props => {
  const [companyName, setCompanyName] = useState('')

  const { business_list } = useSelector(state => state.apiResponseReducers.dashboardInfo)

  const [loadingCodat, setLoadingCodat] = useState(false)

  const onSubmitForm = useCallback(async () => {
    try {
      setLoadingCodat(true)
      const res = await api.connectApp(companyName, business_list[0]?.company_id)
      if (res?.details?.redirect_uri) {
        window.location.href = res?.details?.redirect_uri
      }
    } catch (err) { }
    props.setModalOpen(false)
  }, [props, companyName, business_list])

  const onCloseModal = useCallback(() => {
    props.setModalOpen(false)
  }, [props])

  return (
    <div>
      <Dialog fullWidth={true} open={props.isModalOpen} maxWidth="lg" onClose={onCloseModal}>
        {!loadingCodat && (
          <Box sx={sx.dialogWrapper} className={`row container-fluid`} style={{ paddingLeft: 0 }}>
            <div className="d-none d-md-block flex-column col-md-6 h-100">
              <Box sx={sx.leftBlock} className={`p-5 h-100 d-flex flex-column`}>
                <a
                  href="https://dashboard.app.markaaz.com/"
                  className="text-black fw-bold text-decoration-none fs-5 d-inline-block mb-5"
                >
                  Markaaz
                </a>
                <h2 className="text-black mt-3">
                  Share your financial
                  <br />
                  information
                </h2>
                <p>
                  You will be asked to sign in to your accounts in order to authorize the sharing of your financial
                  information
                </p>
                <div className="d-flex align-items-center mt-auto">
                  <Box sx={sx.shieldIcon} className={`d-flex align-items-center justify-content-center`}>
                    <span>
                      <VerifiedUserIcon className="text-white" />
                    </span>
                  </Box>
                  <div className="ms-3">
                    <span className="d-block fw-bold text-black">We keep your data secure</span>
                    <Link sx={sx.dialogLink} href="https://markaaz.com/terms-and-conditions/" target="_blank" rel="noreferrer" className={`text-decoration-none`}>
                      Learn more
                    </Link>
                  </div>
                </div>
              </Box>
            </div>
            <div className="col-12 col-md-6 h-100">
              <div className="p-5 d-flex flex-column h-100">
                <Box sx={sx.dialogCloseButton}>
                  <KButton fillMode="flat" icon="close" size="small" onClick={onCloseModal} />
                </Box>
                <span className="d-inline-block fw-bold fs-4 my-md-4 mb-4">Enter your business name</span>
                <Form
                  initialValues={{
                    companyName: '',
                  }}
                  onSubmit={onSubmitForm}
                  render={formRenderProps => (
                    <FormElement className="h-100">
                      <fieldset className="k-form-fieldset h-100 d-flex flex-column">
                        <Field
                          id={'companyName'}
                          name={'companyName'}
                          label={'Business Name:'}
                          component={FormInput}
                          validator={inputValidator}
                          value={companyName}
                          onChange={e => setCompanyName(e.target.value)}
                        />
                        <div className="k-form-buttons k-justify-content-end mt-auto">
                          <Button
                            sx={sx.submitButton}
                            className={`fw-bold`}
                            submit
                            type={formRenderProps.valid ? PRIMARY_BUTTON : DISABLED_BUTTON}
                            disabled={!formRenderProps.valid}
                          >
                            Next
                          </Button>
                        </div>
                      </fieldset>
                    </FormElement>
                  )}
                />
              </div>
            </div>
          </Box>
        )}
        {loadingCodat && (
          <Box sx={sx.dialogWrapper} className={`row`}>
            <div className="text-center">
              <Loader type="converging-spinner" />
            </div>
          </Box>
        )}
      </Dialog>
    </div>
  )
}

export default ConnectAppModal
